import { TokenCostInGBP } from '@daymade/api-contracts';
import { exhaustiveCheck } from '@daymade/common-lib/types';
import RoundedButton from 'components/RoundedButton';
import { useState } from 'react';
import { Dropdown, Message } from 'semantic-ui-react';
import { tokenApiClient } from 'utils/apiClient';
import {
  formattedPriceStringFromPence,
  getSpendLimitErrorMessage,
} from 'utils/common';
import HttpStatusCode from 'utils/HttpStatusCode';
import { TPurchaseTokensResponse } from '.';
import RetroIconSVG from '../RetroIconSVG';

const getTokenOrderCost = (numTokens: number) => {
  const PRICE_PER_TOKEN = 50;
  const tokenCost = formattedPriceStringFromPence(numTokens * PRICE_PER_TOKEN);
  return tokenCost.replace('.00', '');
};

interface IQuantitySelect {
  tokenBalance: number;
  handleTokenOrderCreated: (tokenOrder: TPurchaseTokensResponse) => void;
}

export const QuantitySelect = ({
  tokenBalance,
  handleTokenOrderCreated,
}: IQuantitySelect) => {
  const [selectedQuantity, setSelectedQuantity] = useState<number | undefined>(
    undefined
  );
  const [checkoutButtonLoading, setCheckoutButtonLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropdownOptions = [...Array(5)].map((_, idx) => {
    const quantity = (idx + 1) * 10; // Buy in bundles of 10 tokens
    return {
      key: quantity,
      text: `${quantity} token${
        quantity === 1 ? '' : 's'
      } – ${getTokenOrderCost(quantity)}`,
      value: quantity,
    };
  });

  const startCheckoutOrder = async () => {
    setCheckoutButtonLoading(true);
    const tokenAmount = selectedQuantity ? selectedQuantity : 0;

    const resp = await tokenApiClient.auth.purchaseTokens({
      body: {
        tokenAmount: tokenAmount,
        expectedCost: TokenCostInGBP * tokenAmount,
      },
    });

    if (resp.status === HttpStatusCode.OK) {
      handleTokenOrderCreated(resp.body);
    } else if (resp.status === HttpStatusCode.BAD_REQUEST) {
      setCheckoutButtonLoading(false);
      if (resp.body.reason) {
        switch (resp.body.reason) {
          case 'WOULD_EXCEED_SPEND_LIMIT':
            setError(getSpendLimitErrorMessage('purchase tokens'));
            break;
          default:
            exhaustiveCheck(resp.body.reason);
        }
      } else {
        setError('Unable to purchase tokens, try again later.');
      }
    } else if (resp.status === HttpStatusCode.UNAUTHORIZED) {
      setCheckoutButtonLoading(false);
      setError('You must be logged in to purchase tokens.');
    }
  };

  return (
    <div className="quantitySelect">
      <RetroIconSVG fname="arcade-token-greenshadow" />

      <h3>Purchase tokens</h3>
      <p>You currently have {tokenBalance} tokens</p>
      <p>Want to purchase more? Select how many you'd like below.</p>

      <Dropdown
        className="quantitySelectDropdown"
        placeholder="Select tokens"
        selection
        fluid
        scrolling
        options={dropdownOptions}
        value={selectedQuantity}
        onChange={(_e, { value }) => setSelectedQuantity(value as number)}
        style={{ margin: '3em 0 2em' }}
      />

      <RoundedButton
        disabled={!selectedQuantity}
        loading={checkoutButtonLoading}
        onClick={startCheckoutOrder}
      >
        Checkout
      </RoundedButton>

      {error && <Message negative>{error}</Message>}

      <p className="quantitySelectNote">
        PS: We plant a tree with veritree with your purchase!
      </p>

      <style jsx>
        {`
          :global(.quantitySelect .ui.selection.active.dropdown .menu) {
            height: 110px;
          }

          :global(
              .quantitySelect
                .ui.selection.active.dropdown
                .menu::-webkit-scrollbar-thumb
            ) {
            border-radius: 0;
            height: 20px !important;
          }

          :global(.quantitySelect .ui.dropdown:not(.icon)) {
            margin: 1.5em 0;
            border: 3px solid #e1e1e1 !important;
            border-radius: 2px !important;
            padding: 1.5em;
            font-family: 'Press Start 2P', 'Montserrat', sans-serif !important;
            font-size: 0.9em;
          }

          :global(.quantitySelect .ui.dropdown .item) {
            font-size: 0.9em;
          }

          :global(.quantitySelect .ui.selection.active.dropdown:hover .menu) {
            outline: 2px solid #e1e1e1;
            border: 1px solid #e1e1e1;
          }

          :global(.quantitySelect .ui.fluid.dropdown > .dropdown.icon) {
            font-size: 2em;
            padding: 0.4em;
          }

          .quantitySelectNote {
            margin-top: 2em;
            font-size: 0.9rem;
          }
        `}
      </style>
    </div>
  );
};
